import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useMessage } from '../contexts/MessageContext';
import { categoriasConfig, getEtiquetasPorCategoria } from '../config/categoriasConfig';
import buildApiUrl from '../config/apiUrlHelper';
import buildSoundUrl from '../config/soundUrlHelper';
import ConcursoConfiguracion from './ConcursoConfiguracion';
import ConcursoVistaPrevia from './ConcursoVistaPrevia';

const ConcursoPreview = () => {
  const navigate = useNavigate();
  const { showMessage } = useMessage();
  const [categoriasSeleccionadas, setCategoriasSeleccionadas] = useState(Object.keys(categoriasConfig));
  const [etiquetasSeleccionadas, setEtiquetasSeleccionadas] = useState([]);
  const [categoriasExpandidas, setCategoriasExpandidas] = useState({});

  // Función auxiliar para obtener todas las etiquetas posibles para las categorías seleccionadas
  const obtenerTodasLasEtiquetasSeleccionables = () => {
    const etiquetasSeleccionables = [];
    categoriasSeleccionadas.forEach(categoria => {
      etiquetasSeleccionables.push(...getEtiquetasPorCategoria(categoria));
    });
    return etiquetasSeleccionables;
  };
  
  const [cantidadPreguntas, setCantidadPreguntas] = useState(12);
  const [duracionConcurso, setDuracionConcurso] = useState(0); // en segundos, 0 significa sin límite
  const [preguntasPorDificultad, setPreguntasPorDificultad] = useState({ facil: 5, medio: 4, dificil: 3 });
  const [distribucionAutomatica, setDistribucionAutomatica] = useState(false); // Para rastrear si se debe calcular automáticamente
  const [estadisticas, setEstadisticas] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [preguntasPrevista, setPreguntasPrevista] = useState([]);
  
  // Función para obtener estadísticas de preguntas
  const obtenerEstadisticas = useCallback(async () => {
    // Verificar que haya categorías o etiquetas seleccionadas
    if (categoriasSeleccionadas.length === 0 && etiquetasSeleccionadas.length === 0) {
      setEstadisticas([]);
      setPreguntasPrevista([]);
      return;
    }

    setCargando(true);
    
    try {
      const apiUrl = buildApiUrl('obtener_estadisticas_concurso.php');
      console.log('Solicitando estadísticas con:', {
        categorias: categoriasSeleccionadas,
        etiquetas: etiquetasSeleccionadas,
        cantidad: cantidadPreguntas
      });
      const response = await axios.post(apiUrl, {
        categorias: categoriasSeleccionadas,
        etiquetas: etiquetasSeleccionadas,
        cantidad: cantidadPreguntas
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Respuesta de estadísticas:', response.data);
      
      if (response.data && response.data.estadisticas) {
        setEstadisticas(response.data.estadisticas);
      } else {
        console.error('La respuesta no contiene estadísticas válidas:', response.data);
        setEstadisticas([]);
      }
      
      // Actualizar también las preguntas mostradas en la vista previa
      if (response.data && response.data.preguntas) {
        setPreguntasPrevista(response.data.preguntas);
        // Almacenar las preguntas en sessionStorage para que estén disponibles cuando se inicie el concurso
        sessionStorage.setItem('previewPreguntas', JSON.stringify(response.data.preguntas));
        sessionStorage.setItem('previewConfig', JSON.stringify({
          categorias: categoriasSeleccionadas,
          etiquetas: etiquetasSeleccionadas,
          cantidad: cantidadPreguntas,
          duracion: duracionConcurso,
          preguntas_por_dificultad: preguntasPorDificultad
        }));
      } else {
        console.error('La respuesta no contiene preguntas válidas:', response.data);
        setPreguntasPrevista([]);
        // Limpiar el almacenamiento si no hay preguntas válidas
        sessionStorage.removeItem('previewPreguntas');
        sessionStorage.removeItem('previewConfig');
      }
    } catch (error) {
      console.error('Error al obtener estadísticas:', error);
      console.error('Detalles del error:', error.response?.data || error.message);
      // En caso de error, mostrar mensaje de error en lugar de estadísticas simuladas
      setEstadisticas([]);
      setPreguntasPrevista([]);
    } finally {
      setCargando(false);
    }
  }, [categoriasSeleccionadas, etiquetasSeleccionadas, cantidadPreguntas]);

  // Calcular la distribución automática de dificultad cuando cambia la cantidad de preguntas
  useEffect(() => {
    if (distribucionAutomatica) {
      // Calcular distribución basada en porcentajes: 41% fáciles, 33% normales, 25% difíciles
      const facil = Math.max(0, Math.round(cantidadPreguntas * 0.41));
      const medio = Math.max(0, Math.round(cantidadPreguntas * 0.33));
      const dificil = Math.max(0, Math.round(cantidadPreguntas * 0.25));
      
      // Ajustar por si la suma no coincide exactamente con la cantidad total debido a redondeo
      const totalCalculado = facil + medio + dificil;
      if (totalCalculado !== cantidadPreguntas) {
        const diferencia = cantidadPreguntas - totalCalculado;
        // Ajustar la categoría con mayor porcentaje (fácil en este caso)
        setPreguntasPorDificultad({
          facil: facil + diferencia,
          medio: medio,
          dificil: dificil
        });
      } else {
        setPreguntasPorDificultad({
          facil: facil,
          medio: medio,
          dificil: dificil
        });
      }
    }
  }, [cantidadPreguntas, distribucionAutomatica]);

  // Cargar estadísticas en tiempo real cuando cambian las selecciones
  useEffect(() => {
    // Asegurarse de que las categorías predeterminadas estén completamente inicializadas antes de llamar a obtenerEstadisticas
    if (categoriasSeleccionadas.length > 0 || etiquetasSeleccionadas.length > 0) {
      obtenerEstadisticas();
    } else {
      // Si no hay selecciones, usar las categorías predeterminadas
      const defaultCategories = Object.keys(categoriasConfig);
      if (defaultCategories.length > 0) {
        setCategoriasSeleccionadas(defaultCategories);
      }
    }
  }, [categoriasSeleccionadas, etiquetasSeleccionadas, cantidadPreguntas, duracionConcurso, preguntasPorDificultad, obtenerEstadisticas]);

 const handleCategoriaChange = (categoria) => {
    if (categoriasSeleccionadas.includes(categoria)) {
      setCategoriasSeleccionadas(categoriasSeleccionadas.filter(c => c !== categoria));
      setEtiquetasSeleccionadas(etiquetasSeleccionadas.filter(e => !getEtiquetasPorCategoria(categoria).includes(e)));
    } else {
      setCategoriasSeleccionadas([...categoriasSeleccionadas, categoria]);
    }
  };

  const handleEtiquetaChange = (etiqueta) => {
    if (etiquetasSeleccionadas.includes(etiqueta)) {
      setEtiquetasSeleccionadas(etiquetasSeleccionadas.filter(e => e !== etiqueta));
    } else {
      setEtiquetasSeleccionadas([...etiquetasSeleccionadas, etiqueta]);
    }
  };

  const iniciarConcurso = async () => {
    try {
      // Validar que haya categorías o etiquetas seleccionadas
      if (categoriasSeleccionadas.length === 0 && etiquetasSeleccionadas.length === 0) {
        showMessage('Debe seleccionar al menos una categoría o etiqueta para iniciar el concurso.', 'error');
        return;
      }

      // Verificar si hay preguntas de preview almacenadas en sessionStorage
      const previewPreguntas = sessionStorage.getItem('previewPreguntas');
      const previewConfig = sessionStorage.getItem('previewConfig');

      if (previewPreguntas) {
        // Usar las preguntas almacenadas en sessionStorage para garantizar consistencia con la preview
        const preguntas = JSON.parse(previewPreguntas);
        const config = previewConfig ? JSON.parse(previewConfig) : null;

        // Verificar que las preguntas almacenadas coincidan con la configuración actual
        if (config) {
          // Comparar configuraciones para asegurar consistencia
          const configCoincidente = 
            JSON.stringify(config.categorias.sort()) === JSON.stringify(categoriasSeleccionadas.sort()) &&
            JSON.stringify(config.etiquetas.sort()) === JSON.stringify(etiquetasSeleccionadas.sort()) &&
            config.cantidad === cantidadPreguntas &&
            JSON.stringify(config.preguntas_por_dificultad) === JSON.stringify(preguntasPorDificultad);

          if (configCoincidente) {
            // Las preguntas almacenadas coinciden con la configuración actual, usarlas directamente
            // Hacer la llamada al backend para crear el concurso con las preguntas almacenadas
            // Esto asegura que se cree un registro válido en la base de datos
            const apiUrl = buildApiUrl('iniciar_concurso.php');
            const response = await axios.post(apiUrl, {
              categorias: categoriasSeleccionadas,
              etiquetas: etiquetasSeleccionadas,
              cantidad: cantidadPreguntas,
              duracion: duracionConcurso,
              preguntas_por_dificultad: preguntasPorDificultad,
              // Pasar las preguntas almacenadas para que el backend las use si es posible
              use_preview_questions: true,
              preview_questions: preguntas
            });

            if (response.data.success) {
              // Verificar que el número de preguntas recibidas coincida con el solicitado
              if (response.data.preguntas && response.data.preguntas.length !== cantidadPreguntas) {
                const mensaje = `Advertencia: Se esperaban ${cantidadPreguntas} preguntas pero se obtuvieron ${response.data.preguntas.length}.`;
                showMessage(mensaje, 'warning');
                // Mensaje por consola como solicitado
                console.log(`Número esperado de preguntas: ${cantidadPreguntas}, Número recibido de preguntas: ${response.data.preguntas.length}`);
              } else {
                // Mensaje por consola cuando todo es correcto
                console.log(`Número esperado de preguntas: ${cantidadPreguntas}, Número recibido de preguntas: ${response.data.preguntas.length}`);
              }
              
              // Almacenar la duración del concurso y la cantidad de preguntas en sessionStorage para que estén disponibles en Quiz
              sessionStorage.setItem('duracionConcurso', duracionConcurso.toString());
              sessionStorage.setItem('cantidadPreguntas', cantidadPreguntas.toString());
              sessionStorage.setItem('concursoConfig', JSON.stringify({
                categorias: categoriasSeleccionadas,
                etiquetas: etiquetasSeleccionadas,
                cantidad: cantidadPreguntas,
                duracion: duracionConcurso,
                preguntas_por_dificultad: preguntasPorDificultad
              }));
              // Almacenar las preguntas seleccionadas para que se usen en el concurso
              sessionStorage.setItem('preguntasConcurso', JSON.stringify(response.data.preguntas));
              
              // Extraer el ID del concurso de la respuesta y almacenarlo para su uso posterior
              const concursoId = response.data.concurso_id;
              if (concursoId) {
                sessionStorage.setItem('concurso_id', concursoId.toString());
                console.log('Concurso ID almacenado en sessionStorage:', concursoId);
              } else {
                console.warn('No se recibió el ID del concurso en la respuesta');
              }
              
// Reproducir el sonido de tensión con fade in al iniciar el concurso solo después de que la lógica se haya completado exitosamente
              // Creamos un objeto temporal para usar la función de fade in
              const tempAudio = new Audio(buildSoundUrl('/sounds/Gameshow Background Tension.mp3'));
              tempAudio.volume = 0;
              tempAudio.loop = true;
              tempAudio.preload = 'auto';
              tempAudio.load();
              tempAudio.play().catch(e => console.log("Error al reproducir sonido de tensión:", e));
              
              // Aplicar fade in durante 2 segundos (2000ms)
              let volume = 0;
              const fadeInDuration = 2000; // 2 segundos
              const incrementTime = 50; // Incrementar cada 50ms
              const volumeIncrement = (0.3 / (fadeInDuration / incrementTime)); // Incremento proporcional para alcanzar 0.3 en 2 segundos
              
              const fadeInInterval = setInterval(() => {
                volume += volumeIncrement;
                tempAudio.volume = Math.min(volume, 0.3); // Volumen máximo de 0.3
                if (volume >= 0.3) {
                  clearInterval(fadeInInterval);
                }
              }, incrementTime); // Incrementar volumen cada 50ms
              
              navigate('/quiz');
              return; // Salir para evitar la duplicación de lógica
            } else {
              const errorMessage = response.data.error || 'Error desconocido al iniciar el concurso';
              console.error('Error al iniciar concurso (respuesta del servidor):', errorMessage);
              showMessage('Error al iniciar el concurso: ' + errorMessage, 'error');
            }
          }
        }
      }

      // Si no hay preguntas almacenadas o no coinciden con la configuración actual, proceder con la lógica original
      // Si se han seleccionado categorías pero no etiquetas específicas, enviar solo categorías
      // El backend se encargará de incluir preguntas de todas las etiquetas de las categorías seleccionadas
      const categoriasSeleccionadasActual = [...categoriasSeleccionadas];
      const etiquetasSeleccionadasActual = [...etiquetasSeleccionadas];
      
      // Si hay categorías seleccionadas pero ninguna etiqueta, el backend incluirá preguntas de todas las etiquetas de esas categorías
      const apiUrl = buildApiUrl('iniciar_concurso.php');
      console.log('Enviando solicitud para iniciar concurso con:', {
        categorias: categoriasSeleccionadasActual,
        etiquetas: etiquetasSeleccionadasActual,
        cantidad: cantidadPreguntas,
        duracion: duracionConcurso,
        preguntas_por_dificultad: preguntasPorDificultad
      });
      const response = await axios.post(apiUrl, {
        categorias: categoriasSeleccionadasActual,
        etiquetas: etiquetasSeleccionadasActual,
        cantidad: cantidadPreguntas,
        duracion: duracionConcurso,
        preguntas_por_dificultad: preguntasPorDificultad
      });
      
      console.log('Respuesta al iniciar concurso:', response.data);
      
      if (response.data.success) {
        // Verificar que el número de preguntas recibidas coincida con el solicitado
        if (response.data.preguntas && response.data.preguntas.length !== cantidadPreguntas) {
          const mensaje = `Advertencia: Se esperaban ${cantidadPreguntas} preguntas pero se obtuvieron ${response.data.preguntas.length}.`;
          showMessage(mensaje, 'warning');
          // Mensaje por consola como solicitado
          console.log(`Número esperado de preguntas: ${cantidadPreguntas}, Número recibido de preguntas: ${response.data.preguntas.length}`);
        } else {
          // Mensaje por consola cuando todo es correcto
          console.log(`Número esperado de preguntas: ${cantidadPreguntas}, Número recibido de preguntas: ${response.data.preguntas.length}`);
        }
        
        // Almacenar la duración del concurso y la cantidad de preguntas en sessionStorage para que estén disponibles en Quiz
        sessionStorage.setItem('duracionConcurso', duracionConcurso.toString());
        sessionStorage.setItem('cantidadPreguntas', cantidadPreguntas.toString());
        sessionStorage.setItem('concursoConfig', JSON.stringify({
          categorias: categoriasSeleccionadasActual,
          etiquetas: etiquetasSeleccionadasActual,
          cantidad: cantidadPreguntas,
          duracion: duracionConcurso,
          preguntas_por_dificultad: preguntasPorDificultad
        }));
        // Almacenar las preguntas seleccionadas para que se usen en el concurso
        sessionStorage.setItem('preguntasConcurso', JSON.stringify(response.data.preguntas));
        
        // Extraer el ID del concurso de la respuesta y almacenarlo para su uso posterior
        const concursoId = response.data.concurso_id;
        if (concursoId) {
          sessionStorage.setItem('concurso_id', concursoId.toString());
          console.log('Concurso ID almacenado en sessionStorage:', concursoId);
        } else {
          console.warn('No se recibió el ID del concurso en la respuesta');
        }
        
// Reproducir el sonido de tensión con fade in al iniciar el concurso solo después de que la lógica se haya completada exitosamente
        // Creamos un objeto temporal para usar la función de fade in
        const tempAudio = new Audio(buildSoundUrl('/sounds/Gameshow Background Tension.mp3'));
        tempAudio.volume = 0;
        tempAudio.loop = true;
        tempAudio.preload = 'auto';
        tempAudio.load();
        tempAudio.play().catch(e => console.log("Error al reproducir sonido de tensión:", e));
        
        // Aplicar fade in durante 2 segundos (2000ms)
        let volume = 0;
        const fadeInDuration = 2000; // 2 segundos
        const incrementTime = 50; // Incrementar cada 50ms
        const volumeIncrement = (0.3 / (fadeInDuration / incrementTime)); // Incremento proporcional para alcanzar 0.3 en 2 segundos
        
        const fadeInInterval = setInterval(() => {
          volume += volumeIncrement;
          tempAudio.volume = Math.min(volume, 0.3); // Volumen máximo de 0.3
          if (volume >= 0.3) {
            clearInterval(fadeInInterval);
          }
        }, incrementTime); // Incrementar volumen cada 50ms
        
        navigate('/quiz');
      } else {
        const errorMessage = response.data.error || 'Error desconocido al iniciar el concurso';
        console.error('Error al iniciar concurso (respuesta del servidor):', errorMessage);
        showMessage('Error al iniciar el concurso: ' + errorMessage, 'error');
      }
    } catch (error) {
      console.error('Error al iniciar concurso (excepción):', error);
      console.error('Detalles del error:', error.response?.data || error.message || error);
      showMessage('Error al iniciar el concurso: ' + (error.response?.data?.error || error.message || 'Error desconocido'), 'error');
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 dark:bg-gray-900 dark:text-white transition-colors duration-300">
      <h2 className="text-2xl sm:text-3xl font-bold mb-6 text-center dark:text-white">Preview del Concurso</h2>
      <div className="flex flex-col xl:flex-row gap-6 sm:gap-8">
        <div className="flex-1 min-w-0">
          <ConcursoConfiguracion
            categoriasSeleccionadas={categoriasSeleccionadas}
            etiquetasSeleccionadas={etiquetasSeleccionadas}
            categoriasExpandidas={categoriasExpandidas}
            cantidadPreguntas={cantidadPreguntas}
            duracionConcurso={duracionConcurso}
            preguntasPorDificultad={preguntasPorDificultad}
            distribucionAutomatica={distribucionAutomatica}
            setCategoriasSeleccionadas={setCategoriasSeleccionadas}
            setEtiquetasSeleccionadas={setEtiquetasSeleccionadas}
            setCategoriasExpandidas={setCategoriasExpandidas}
            setCantidadPreguntas={setCantidadPreguntas}
            setDuracionConcurso={setDuracionConcurso}
            setPreguntasPorDificultad={setPreguntasPorDificultad}
            setDistribucionAutomatica={setDistribucionAutomatica}
            handleCategoriaChange={handleCategoriaChange}
            handleEtiquetaChange={handleEtiquetaChange}
            iniciarConcurso={iniciarConcurso}
            obtenerTodasLasEtiquetasSeleccionables={obtenerTodasLasEtiquetasSeleccionables}
          />
        </div>
        <div className="w-full xl:w-1/3 min-w-0">
          <ConcursoVistaPrevia
            estadisticas={estadisticas}
            cargando={cargando}
            preguntas={preguntasPrevista}
          />
        </div>
      </div>
    </div>
  );
};

export default ConcursoPreview;
